/**
 * Custom Sticky Add to Cart
 */

class StickyCartEnhancer {
  constructor() {
    this.instances = new Map();
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.enhanceExistingSections());
    } else {
      this.enhanceExistingSections();
    }

    this.observeForNewSections();
  }

  enhanceExistingSections() {
    const stickyCartSections = document.querySelectorAll('[data-sticky-cart]');
    
    stickyCartSections.forEach(section => {
      if (!this.instances.has(section)) {
        this.enhanceSection(section);
      }
    });
  }

  enhanceSection(container) {
    const instance = new StickyCartInstance(container);
    this.instances.set(container, instance);
    
    container.setAttribute('data-enhanced', 'true');
  }

  observeForNewSections() {
    const observer = new MutationObserver((mutations) => {
      let hasNewSections = false;
      
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            if (node.matches && node.matches('[data-sticky-cart]:not([data-enhanced])')) {
              hasNewSections = true;
            } else if (node.querySelector) {
              const newSection = node.querySelector('[data-sticky-cart]:not([data-enhanced])');
              if (newSection) {
                hasNewSections = true;
              }
            }
          }
        });

        mutation.removedNodes.forEach((node) => {
          if (node.nodeType === 1 && this.instances.has(node)) {
            const instance = this.instances.get(node);
            instance.cleanup();
            this.instances.delete(node);
          }
        });
      });
      
      if (hasNewSections) {
        setTimeout(() => this.enhanceExistingSections(), 10);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
}

class StickyCartInstance {
  constructor(container) {
    this.container = container;
    this.abortController = new AbortController();
    this.variantChangeTimeout = null;
    this.currentVariantId = null;
    this.variants = [];
    this.footerElement = null;
    
    this.init();
  }

  init() {
    this.calculateHeaderHeight();
    this.cacheFooterElement();
    
    try {
      this.variants = JSON.parse(this.container.dataset.variants || '[]');
    } catch (e) {
      console.error('Error parsing variants:', e);
      return;
    }

    const { signal } = this.abortController;
    
    window.addEventListener('scroll', this.handleScroll, { signal, passive: true });
    window.addEventListener('resize', this.calculateHeaderHeight, { signal, passive: true });
    
    document.addEventListener('variant:update', this.handleProductPageVariantUpdate, { signal });
    document.addEventListener('variant:selected', this.handleVariantSelected, { signal });
    
    this.setupVariantSelects();
    this.setupAddToCartButton();
    this.initializeCurrentVariant();
    this.handleScroll();
  }

  cleanup() {
    this.abortController.abort();
    if (this.variantChangeTimeout) {
      clearTimeout(this.variantChangeTimeout);
    }
  }

  cacheFooterElement() {
    this.footerElement = document.querySelector('footer, .footer, #footer, [role="contentinfo"]');
  }

  calculateHeaderHeight = () => {
    const stickySection = document.querySelector('.sticky-add-to-cart-section.sticky-position-top');
    if (!stickySection) return;
    
    const header = document.querySelector('#header-component, header, .header');
    if (header) {
      const headerHeight = header.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
    }
  };

  handleScroll = () => {
    const shouldShow = this.checkShowTrigger();
    const shouldHide = this.checkHideTrigger();
    
    const isVisible = shouldShow && !shouldHide;
    this.container.classList.toggle('is-visible', isVisible);
  };

  checkShowTrigger() {
    const triggerButton = document.querySelector('.button.add-to-cart-button');
    const mode = this.container.dataset.scrollTrigger;
    const pixelOffset = parseInt(this.container.dataset.scrollOffset, 10) || 0;
    
    if (mode === 'button' && triggerButton) {
      const rect = triggerButton.getBoundingClientRect();
      const triggerPoint = rect.top + window.scrollY + triggerButton.offsetHeight;
      return window.scrollY > triggerPoint;
    } else if (mode === 'scroll_trigger') {
      return window.scrollY > pixelOffset;
    }
    
    return false;
  }

  checkHideTrigger() {
    const hideMode = this.container.dataset.scrollHideTrigger;
    
    switch (hideMode) {
      case 'footer':
        if (!this.footerElement) return false;
        const footerRect = this.footerElement.getBoundingClientRect();
        return footerRect.top <= window.innerHeight;
        
      case 'bottom':
        const documentHeight = document.documentElement.scrollHeight;
        const windowHeight = window.innerHeight;
        const scrollTop = window.scrollY;
        return scrollTop + windowHeight >= documentHeight - 10;
        
      case 'pixel_offset':
        const hideOffset = parseInt(this.container.dataset.scrollHideOffset, 10) || 300;
        const docHeight = document.documentElement.scrollHeight;
        const winHeight = window.innerHeight;
        const scrollPosition = window.scrollY;
        const distanceFromBottom = docHeight - (scrollPosition + winHeight);
        return distanceFromBottom <= hideOffset;
        
      case 'custom_element':
        const elementId = this.container.dataset.hideElementId;
        if (!elementId) return false;
        
        const customElement = document.getElementById(elementId);
        if (!customElement) return false;
        
        const elementRect = customElement.getBoundingClientRect();
        return elementRect.top <= window.innerHeight;
        
      case 'never':
      default:
        return false;
    }
  }

  setupVariantSelects() {
    const variantSelects = this.container.querySelectorAll('.sticky-variant-select');
    
    variantSelects.forEach(select => {
      select.addEventListener('change', () => {
        clearTimeout(this.variantChangeTimeout);
        this.variantChangeTimeout = setTimeout(() => {
          this.handleVariantChange(false);
        }, 100);
      }, { signal: this.abortController.signal });
    });
  }

  setupAddToCartButton() {
    const button = this.container.querySelector('.sticky-add-to-cart');
    if (button) {
      button.addEventListener('click', this.addToCart, { 
        signal: this.abortController.signal 
      });
    }
  }

  handleVariantChange(fromProductPage = false) {
    const variantSelects = this.container.querySelectorAll('.sticky-variant-select');
    const selectedValues = Array.from(variantSelects).map(select => select.value);
    
    const matchingVariant = this.variants.find(variant => {
      const variantOptions = [variant.option1, variant.option2, variant.option3].filter(Boolean);
      return selectedValues.every((value, index) => variantOptions[index] === value);
    });
    
    if (matchingVariant) {
      this.updateVariantDisplay(matchingVariant);
      
      if (!fromProductPage && this.currentVariantId !== matchingVariant.id) {
        this.currentVariantId = matchingVariant.id;
        this.dispatchStickyVariantEvent(matchingVariant);
      }
    }
  }

  handleProductPageVariantUpdate = (event) => {
    if (event.detail?.data?.productId !== this.container.dataset.productId) return;
    
    const variant = event.detail.resource;
    if (!variant || !variant.id) return;
    
    if (this.currentVariantId === String(variant.id)) return;
    
    this.currentVariantId = String(variant.id);
    
    const matchingVariant = this.variants.find(v => String(v.id) === String(variant.id));
    if (!matchingVariant) return;
    
    this.updateSelectsFromVariant(matchingVariant);
    this.updateVariantDisplay(matchingVariant);
  };

  handleVariantSelected = (event) => {
    if (event.target === this.container) return;
    
    const variantId = event.detail?.resource?.id;
    if (!variantId) return;
    
    const matchingVariant = this.variants.find(v => String(v.id) === String(variantId));
    if (matchingVariant && this.currentVariantId !== String(variantId)) {
      this.currentVariantId = String(variantId);
      this.updateSelectsFromVariant(matchingVariant);
      this.updateVariantDisplay(matchingVariant);
    }
  };

  dispatchStickyVariantEvent(variant) {
    const variantSelectedEvent = new CustomEvent('variant:selected', {
      bubbles: true,
      detail: {
        resource: { id: String(variant.id) }
      }
    });
    
    document.dispatchEvent(variantSelectedEvent);
    
    if (window.Theme?.template?.name === 'product') {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url.toString());
    }
  }

  updateSelectsFromVariant(variant) {
    const variantSelects = this.container.querySelectorAll('.sticky-variant-select');
    const options = [variant.option1, variant.option2, variant.option3].filter(Boolean);
    
    variantSelects.forEach((select, index) => {
      if (options[index]) {
        select.value = options[index];
      }
    });
  }

  updateVariantDisplay(variant) {
    const button = this.container.querySelector('.sticky-add-to-cart');
    if (!button) return;
    
    button.dataset.productId = variant.id;
    
    const priceWrapper = this.container.querySelector('.sticky-product-price');
    if (priceWrapper) {
      this.updatePriceDisplay(priceWrapper, variant);
    }
    
    const productImage = this.container.querySelector('.sticky-product-image');
    if (variant.featured_image && productImage) {
      const newImageUrl = variant.featured_image;
      if (newImageUrl && productImage.src !== newImageUrl) {
        productImage.src = newImageUrl;
      }
    }
    
    this.updateButtonState(variant);
  }

  updatePriceDisplay(priceWrapper, variant) {
    const regularPrice = priceWrapper.querySelector('.sticky-price-regular');
    const salePrice = priceWrapper.querySelector('.sticky-price-sale');
    const comparePrice = priceWrapper.querySelector('.sticky-price-compare');
    
    const formatPrice = (cents) => {
      if (window.Shopify && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(cents);
      }
      return '€' + (cents / 100).toFixed(2);
    };
    
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      if (salePrice) {
        salePrice.textContent = formatPrice(variant.price);
      } else if (regularPrice) {
        regularPrice.textContent = formatPrice(variant.price);
      }
      
      if (comparePrice) {
        comparePrice.textContent = formatPrice(variant.compare_at_price);
        comparePrice.style.display = '';
      }
    } else {
      if (regularPrice) {
        regularPrice.textContent = formatPrice(variant.price);
      }
      if (comparePrice) {
        comparePrice.style.display = 'none';
      }
    }
  }

  updateButtonState(variant) {
    const button = this.container.querySelector('.sticky-add-to-cart');
    if (!button) return;
    
    const buttonText = button.querySelector('.sticky-add-to-cart-text');
    const originalButtonText = button.dataset.originalText || 'Add to Cart';
    const productTitle = this.container.dataset.productTitle || '';
    
    if (variant.available) {
      button.disabled = false;
      if (buttonText) buttonText.textContent = originalButtonText;
      button.setAttribute('aria-label', `Add ${productTitle} to cart`);
    } else {
      button.disabled = true;
      if (buttonText) buttonText.textContent = 'Sold Out';
      button.setAttribute('aria-label', `${productTitle} is sold out`);
    }
  }

  initializeCurrentVariant() {
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariantId = urlParams.get('variant');
    
    if (urlVariantId) {
      const urlVariant = this.variants.find(v => String(v.id) === String(urlVariantId));
      if (urlVariant) {
        this.currentVariantId = String(urlVariantId);
        this.updateSelectsFromVariant(urlVariant);
        this.updateVariantDisplay(urlVariant);
        return;
      }
    }
    
    this.handleVariantChange(true);
  }

  addToCart = () => {
    const button = this.container.querySelector('.sticky-add-to-cart');
    if (!button) return;
    
    const variantId = button.dataset.productId;
    if (!variantId) {
      console.error('No variant ID found');
      return;
    }
    
    const selectedVariant = this.variants.find(v => String(v.id) === String(variantId));
    if (!selectedVariant) {
      console.error('Variant not found:', variantId);
      this.showOverlay('Selected variant not found. Please try again.');
      return;
    }
    
    const buttonText = button.querySelector('.sticky-add-to-cart-text');
    const originalText = buttonText ? buttonText.textContent : 'Add to Cart';
    
    button.disabled = true;
    if (buttonText) buttonText.textContent = 'Adding...';
    
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    
    const sectionsToUpdate = this.getSectionsToUpdate();
    if (sectionsToUpdate.length > 0) {
      formData.append('sections', sectionsToUpdate.join(','));
    }
    
    this.triggerFlyToCartAnimation();
    
    fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json, text/javascript'
      }
    })
    .then(response => {
      if (!response.ok) {
        return response.json().then(errorData => {
          console.error('Shopify cart error:', errorData);
          this.showOverlay(errorData.message || 'Failed to add to cart');
          throw new Error(errorData.message || 'Failed to add to cart');
        });
      }
      return response.json();
    })
    .then(response => {
      if (buttonText) buttonText.textContent = 'Added!';
      
      const sections = response.sections || {};
      Object.keys(sections).forEach(sectionId => {
        const sectionElement = document.getElementById(`shopify-section-${sectionId}`);
        if (sectionElement && sections[sectionId]) {
          if (window.morphSection && typeof window.morphSection === 'function') {
            window.morphSection(sectionId, sections[sectionId]);
          } else {
            sectionElement.innerHTML = sections[sectionId];
          }
        }
      });
      
      this.dispatchCartEvents(response, sections);
      this.updateCartAndOpenDrawer();
      
      setTimeout(() => {
        button.disabled = false;
        if (buttonText) buttonText.textContent = originalText;
      }, 2000);
    })
    .catch(error => {
      console.error('Cart error:', error);
      
      button.disabled = false;
      if (buttonText) buttonText.textContent = originalText;
      
      const errorEvent = new CustomEvent('cart:error', {
        bubbles: true,
        detail: {
          sourceId: this.container.dataset.sectionId || 'sticky-cart',
          data: {
            message: error.message || 'Failed to add item to cart',
            originalError: error
          }
        }
      });
      
      document.dispatchEvent(errorEvent);
    });
  };

  getSectionsToUpdate() {
    const sections = [];
    
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    cartItemsComponents.forEach(component => {
      if (component.dataset.sectionId) {
        sections.push(component.dataset.sectionId);
      }
    });
    
    const cartIconSection = document.querySelector('.header-actions__cart-icon')?.closest('[id^="shopify-section-"]');
    if (cartIconSection) {
      const sectionId = cartIconSection.id.replace('shopify-section-', '');
      if (sectionId) sections.push(sectionId);
    }
    
    return sections;
  }

  showOverlay(message, duration = 3000) {
    let overlay = document.getElementById('messageOverlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'messageOverlay';
      document.body.appendChild(overlay);
    }
    overlay.textContent = message;
    overlay.classList.add('show');
    setTimeout(() => overlay.classList.remove('show'), duration);
  }

  triggerFlyToCartAnimation() {
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const button = this.container.querySelector('.sticky-add-to-cart');
    const productImage = this.container.querySelector('.sticky-product-image');
    
    if (!cartIcon || !button || !productImage) return;
    
    const flyToCartElement = document.createElement('fly-to-cart');
    const imageUrl = productImage.src || productImage.dataset.src;
    
    if (imageUrl) {
      flyToCartElement.style.setProperty('background-image', `url(${imageUrl})`);
    }
    
    flyToCartElement.source = button;
    flyToCartElement.destination = cartIcon;
    document.body.appendChild(flyToCartElement);
  }

  dispatchCartEvents(response, sections) {
    const cartAddEvent = new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: response,
        sourceId: this.container.dataset.sectionId || 'sticky-cart',
        data: {
          source: 'sticky-cart-component',
          itemCount: 1,
          productId: this.container.dataset.productId,
          variantId: response.variant_id,
          sections: sections,
          didError: false
        }
      }
    });
    
    document.dispatchEvent(cartAddEvent);
  }

  updateCartAndOpenDrawer() {
    fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        this.updateCartIconCount(cart.item_count);
        
        setTimeout(() => {
          this.openCartDrawer();
        }, 600);
      })
      .catch(error => {
        console.error('Error fetching updated cart:', error);
      });
  }

  updateCartIconCount(itemCount) {
    const cartCountElements = document.querySelectorAll('.cart-bubble__text-count, .cart-count-bubble, [data-cart-count]');
    
    cartCountElements.forEach(element => {
      if (element) {
        element.textContent = itemCount;
        
        const bubble = element.closest('.cart-bubble');
        if (bubble) {
          bubble.classList.toggle('visually-hidden', itemCount === 0);
        }
      }
    });
    
    const cartIcons = document.querySelectorAll('.header-actions__cart-icon');
    cartIcons.forEach(icon => {
      icon.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);
    });
    
    sessionStorage.setItem('cart-count', JSON.stringify({
      value: String(itemCount),
      timestamp: Date.now()
    }));
  }

  openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer-component, cart-drawer, [data-cart-drawer]');
    
    if (cartDrawer) {
      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      } else if (typeof cartDrawer.showDialog === 'function') {
        cartDrawer.showDialog();
      } else {
        const cartTrigger = document.querySelector('[data-cart-trigger], .header-actions__cart');
        cartTrigger?.click();
      }
    }
  }
}

new StickyCartEnhancer();